package ai.wagemix.web.screens.jobdescriptions

import ai.wagemix.web.theme.WMTightGapVar
import com.varabyte.kobweb.compose.ui.toAttrs
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.dom.A
import ai.wagemix.web.screens.WMLockedState
import com.varabyte.kobweb.compose.ui.modifiers.textDecorationLine
import com.varabyte.kobweb.compose.css.TextDecorationLine
import ai.wagemix.net.models.WMUploadFile
import ai.wagemix.net.models.of
import ai.wagemix.net.models.WMUploadProgress
import ai.wagemix.logic.WMRoutes
import ai.wagemix.logic.jobdescriptions.WMFileProblem
import ai.wagemix.logic.jobdescriptions.WMJobBoards
import ai.wagemix.logic.jobdescriptions.WMJobDescriptionsPresenter
import ai.wagemix.logic.jobdescriptions.WMJobDescriptionsPresenter.Action
import ai.wagemix.logic.jobdescriptions.WMJobDescriptionsPresenter.Event
import ai.wagemix.logic.jobdescriptions.WMJobDescriptionsPresenter.Prompt
import ai.wagemix.logic.jobdescriptions.WMUploadQuota
import ai.wagemix.logic.jobdescriptions.WMUrlProblem
import ai.wagemix.net.models.WMUpload
import ai.wagemix.net.models.WMUploadFailureReason
import ai.wagemix.net.models.WMUploadSource
import ai.wagemix.net.models.WMUploadStatus
import ai.wagemix.web.components.WMButton
import ai.wagemix.web.components.WMButtonKind
import ai.wagemix.web.components.WMButtonSize
import ai.wagemix.web.components.WMDialog
import ai.wagemix.web.components.WMDialogButtonStyle
import ai.wagemix.web.components.WMEmptyState
import ai.wagemix.web.components.WMFormat
import ai.wagemix.web.components.WMIconGlyph
import ai.wagemix.web.components.WMMeter
import ai.wagemix.web.components.WMNotice
import ai.wagemix.web.components.WMNoticeKind
import ai.wagemix.web.components.WMPanel
import ai.wagemix.web.components.WMSegmentedControl
import ai.wagemix.web.components.WMTextField
import ai.wagemix.web.components.WMUploadDropzone
import ai.wagemix.web.components.WMUploadRow
import ai.wagemix.web.components.WMUploadRowPresentation
import ai.wagemix.web.components.of
import ai.wagemix.web.components.WMFilePreviewDialog
import ai.wagemix.web.screens.WMWebStrings
import ai.wagemix.web.screens.readBytes
import ai.wagemix.web.theme.WMAppContentGapVar
import ai.wagemix.web.theme.WMBodySmallTextStyle
import ai.wagemix.web.theme.WMCaptionTextStyle
import ai.wagemix.web.theme.WMLabelTextStyle
import androidx.compose.runtime.Composable
import androidx.compose.runtime.State
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import com.fsryan.foundation.logic.FSPrompt
import com.fsryan.foundation.logic.prompting.FSPromptingPresenter
import ai.wagemix.web.screens.WMPromptDialog
import com.fsryan.foundation.web.screens.FSWebScreen
import com.fsryan.foundation.web.theme.fs
import org.jetbrains.compose.web.css.FlexWrap
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.flexWrap
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.maxWidth
import com.varabyte.kobweb.compose.ui.modifiers.whiteSpace
import com.varabyte.kobweb.compose.css.WhiteSpace
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import com.varabyte.kobweb.silk.theme.colors.palette.toPalette
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.px

@Composable
fun <P: Prompt, E: Event<P>, A: Action, PR: WMJobDescriptionsPresenter<P, E, A>, VM: WMBaseJobDescriptionsWebViewModel<P, E, A, PR>> WMJobDescriptionsScreen(
    createViewModel: () -> VM,
    strings: WMWebStrings,
    modifier: Modifier = Modifier
) {
    FSWebScreen(createViewModel = createViewModel) { vm ->
        val scope = rememberCoroutineScope()
        WMJobDescriptionsScreenLayout(
            strings = strings,
            loadingState = vm.loadingState,
            uploadsState = vm.uploadsState,
            quotaState = vm.quotaState,
            activeUploadState = vm.activeUploadState,
            lapsedState = vm.lapsedState,
            modifier = modifier,
            onFile = { file ->
                scope.launch { vm.userDroppedFile(WMUploadFile.of(name = file.name, contentType = file.type, bytes = file.readBytes())) }
            },
            onUrl = vm::userSubmittedUrl,
            onOpenMix = vm::userRequestedOpenMix,
            onViewFile = vm::userRequestedFilePreview,
            onReevaluate = vm::userRequestedReevaluate,
            onRetryUpload = vm::userRequestedRetryUpload,
            onDeleteUpload = vm::userRequestedDeleteUpload
        )
        val prompt = vm.promptState.value
        WMJobDescriptionsPromptDialog(prompt = prompt, strings = strings) { selected -> vm.userRespondedToPrompt(FSPrompt.Response(prompt, selected)) }
    }
}

@Composable
fun WMJobDescriptionsScreenLayout(
    strings: WMWebStrings,
    loadingState: State<Boolean>,
    uploadsState: State<List<WMUpload>>,
    quotaState: State<WMUploadQuota?>,
    activeUploadState: State<WMUploadProgress?>,
    lapsedState: State<Boolean>,
    modifier: Modifier = Modifier,
    onFile: (org.w3c.files.File) -> Unit,
    onUrl: (String) -> Unit,
    onOpenMix: (String) -> Unit,
    onViewFile: (String) -> Unit,
    onReevaluate: (String) -> Unit,
    onRetryUpload: (uploadId: String) -> Unit,
    onDeleteUpload: (uploadId: String) -> Unit
) {
    val j = strings.jobDescriptions
    val colors = ColorMode.current.toPalette().fs
    val quota = quotaState.value
    val exhausted = quota?.exhausted == true
    var tab by remember { mutableStateOf(0) }
    var url by remember { mutableStateOf("") }
    val busy = loadingState.value || activeUploadState.value?.inProgress == true
    if (lapsedState.value) {
        WMLockedState(strings.renewal.uploadsTitle, strings.renewal.uploadsMessage, strings, modifier)
        return
    }

    Column(modifier = Modifier.fillMaxWidth().gap(WMAppContentGapVar.value()).then(modifier)) {
        if (exhausted) {
            WMNotice(j.overQuotaTitle, kind = WMNoticeKind.Warning, message = j.overQuotaMessage) {
                WMButton(strings.common.upgradeToPro, size = WMButtonSize.Sm) { kotlinx.browser.window.location.href = WMRoutes.PRICING }
            }
        }
        WMPanel(
            title = j.title,
            action = {
                quota?.let { q ->
                    val qt = q.quota
                    if (qt == null) SpanText(j.quotaUnlimited, WMLabelTextStyle.toModifier().color(colors.onSurfaceVariant))
                    else Row(modifier = Modifier.gap(8.px).maxWidth(240.px), verticalAlignment = Alignment.CenterVertically) {
                        SpanText("${q.used} / $qt ${j.quota}", WMLabelTextStyle.toModifier().color(colors.onSurfaceVariant).whiteSpace(WhiteSpace.NoWrap))
                        WMMeter(fraction = if (qt == 0) 1.0 else q.used.toDouble() / qt, accessibilityString = "${q.used} / $qt ${j.quota}", warning = q.exhausted)
                    }
                }
            }
        ) {
            WMSegmentedControl(options = listOf(j.uploadFile, j.pasteUrl), selectedIndex = tab, onSelect = { tab = it }, accessibilityString = j.title, modifier = Modifier.maxWidth(320.px))
            when (tab) {
                0 -> {
                    WMUploadDropzone(strings = j.dropzone, enabled = !exhausted && !busy, onFile = onFile)
                    WMPrivacyNote(j)
                }
                else -> Column(modifier = Modifier.fillMaxWidth().gap(8.px)) {
                    Row(modifier = Modifier.fillMaxWidth().gap(8.px).flexWrap(FlexWrap.Wrap), verticalAlignment = Alignment.CenterVertically) {
                        WMTextField(url, onTextChange = { url = it }, placeholder = j.urlPlaceholder, leadingIcon = WMIconGlyph.Link, modifier = Modifier.fillMaxWidth().maxWidth(520.px), enabled = !exhausted && !busy, onCommit = { onUrl(url) })
                        WMButton(j.analyze, enabled = !exhausted && !busy && url.isNotBlank(), leadingIcon = WMIconGlyph.Sparkles) { onUrl(url) }
                    }
                    SpanText("${j.supportedBoards}: ${WMJobBoards.SUPPORTED.joinToString { it.displayName }}", WMCaptionTextStyle.toModifier().color(colors.onSurfaceVariant))
                    WMPrivacyNote(j)
                }
            }
            activeUploadState.value?.let { progress ->
                if (progress.inProgress) WMNotice(j.processing, kind = WMNoticeKind.AI, message = progress.message.takeIf { it.isNotBlank() })
            }
        }

        WMPanel(title = j.uploads) {
            val uploads = uploadsState.value
            if (uploads.isEmpty()) {
                WMEmptyState(WMIconGlyph.FileText, j.emptyTitle, j.emptyMessage)
            } else {
                uploads.forEach { upload ->
                    WMUploadRow(
                        row = WMUploadRowPresentation.of(
                            uploadId = upload.id,
                            title = upload.sourceUrl ?: upload.originalFileName,
                            meta = listOfNotNull(WMFormat.fileSize(upload.size).takeIf { upload.size > 0 }, upload.uploadedAt?.toString()?.substringBefore('T')).joinToString(" · "),
                            status = when (upload.status) {
                                WMUploadStatus.Processing, WMUploadStatus.Unknown -> ai.wagemix.web.components.WMUploadStatus.Processing
                                WMUploadStatus.Ready -> ai.wagemix.web.components.WMUploadStatus.Ready
                                WMUploadStatus.Failed -> ai.wagemix.web.components.WMUploadStatus.Failed
                                WMUploadStatus.Rejected -> ai.wagemix.web.components.WMUploadStatus.Rejected
                            },
                            fromUrl = upload.source == WMUploadSource.Url,
                            mixId = upload.mixId,
                            detail = j.failureDetail(upload, atLimitOf = quota?.quota?.takeIf { exhausted })
                        ),
                        strings = j.row
                    ) {
                        upload.mixId?.let { mixId ->
                            WMButton(j.openMix, size = WMButtonSize.Sm) { onOpenMix(mixId) }
                            // A posting URL is stored as the PDF it was rendered to, so it has a file to view too.
                            WMButton(
                                j.viewFile,
                                kind = WMButtonKind.Ghost,
                                size = WMButtonSize.Sm,
                                leadingIcon = WMIconGlyph.Eye,
                                enabled = !busy
                            ) {
                                onViewFile(mixId)
                            }
                            // A mix already being re-evaluated (its upload is processing) cannot be started again.
                            WMButton(
                                j.reevaluate,
                                kind = WMButtonKind.Ghost,
                                size = WMButtonSize.Sm,
                                leadingIcon = WMIconGlyph.RefreshCw,
                                enabled = !busy && upload.status != WMUploadStatus.Processing
                            ) {
                                onReevaluate(mixId)
                            }
                        }
                        // Retry only where it can help: a rejected upload, or one that was not a job description,
                        // has no file left.
                        // At the plan's limit a retry could make one mix too many, so it waits (plan rule 7).
                        if (upload.status == WMUploadStatus.Failed && upload.failureReason?.retryable != false) {
                            WMButton(
                                j.retryUpload,
                                kind = WMButtonKind.Secondary,
                                size = WMButtonSize.Sm,
                                leadingIcon = WMIconGlyph.RefreshCw,
                                enabled = !busy && !exhausted
                            ) {
                                onRetryUpload(upload.id)
                            }
                        }
                        // A failed upload the user no longer wants can go, file and all (plan rule 8).
                        if (upload.status == WMUploadStatus.Failed && upload.mixId == null) {
                            WMButton(
                                j.deleteUpload,
                                kind = WMButtonKind.Ghost,
                                size = WMButtonSize.Sm,
                                leadingIcon = WMIconGlyph.X,
                                enabled = !busy
                            ) {
                                onDeleteUpload(upload.id)
                            }
                        }
                    }
                }
            }
        }
    }
}

@Composable
fun <P: Prompt> WMJobDescriptionsPromptDialog(prompt: P, strings: WMWebStrings, onResponse: (Int) -> Unit) {
    val j = strings.jobDescriptions
    val c = strings.common
    val ok = { onResponse(0) }
    WMPromptDialog(prompt = prompt, unwrap = { (it as? Prompt.ComposedSuperPrompt)?.wrapped }, strings = strings, onResponse = onResponse) { p ->
        when (p) {
            is Prompt.UploadLimitReached -> WMDialog(
                title = j.limitTitle,
                description = j.limitMessage,
                onDismiss = { onResponse(Prompt.UploadLimitReached.Selection.Dismiss.ordinal) },
                actions = {
                    WMButton(c.dismiss, kind = WMButtonKind.Ghost, modifier = WMDialogButtonStyle.toModifier()) { onResponse(Prompt.UploadLimitReached.Selection.Dismiss.ordinal) }
                    WMButton(c.viewPlans, modifier = WMDialogButtonStyle.toModifier()) { onResponse(Prompt.UploadLimitReached.Selection.ViewPlans.ordinal) }
                }
            ) {}
            is Prompt.InvalidFile -> WMDialog(
                title = j.invalidFileTitle,
                description = when (p.problem) {
                    WMFileProblem.UnsupportedType -> j.unsupportedType
                    WMFileProblem.TooLarge -> j.tooLarge
                    WMFileProblem.Empty -> j.emptyFile
                },
                onDismiss = ok,
                actions = { WMButton(c.close, modifier = WMDialogButtonStyle.toModifier(), onClick = ok) }
            ) {}
            is Prompt.InvalidUrl -> WMDialog(
                title = j.invalidUrlTitle,
                description = when (p.problem) {
                    WMUrlProblem.Blank -> j.urlBlank
                    WMUrlProblem.Malformed -> j.urlMalformed
                    WMUrlProblem.UnsupportedBoard -> j.urlUnsupported
                },
                onDismiss = ok,
                actions = { WMButton(c.close, modifier = WMDialogButtonStyle.toModifier(), onClick = ok) }
            ) {
                if (p.problem == WMUrlProblem.UnsupportedBoard) {
                    SpanText(WMJobBoards.SUPPORTED.joinToString { it.displayName }, WMBodySmallTextStyle.toModifier())
                }
            }
            is Prompt.UploadFailed -> WMDialog(
                title = j.failedTitle,
                description = p.message ?: j.failedMessage,
                onDismiss = ok,
                actions = { WMButton(c.close, modifier = WMDialogButtonStyle.toModifier(), onClick = ok) }
            ) {}
            is Prompt.PreviewFile -> WMFilePreviewDialog(
                fileName = p.file.fileName,
                previewUrl = p.file.previewUrl,
                strings = j.filePreview,
                onDismiss = { onResponse(Prompt.PreviewFile.Selection.Close.ordinal) },
                onDownload = { onResponse(Prompt.PreviewFile.Selection.Download.ordinal) }
            )
            is Prompt.UploadNotAccepted -> WMDialog(
                title = j.title(p.reason),
                description = p.explanation.ifBlank { null },
                onDismiss = ok,
                actions = { WMButton(c.close, modifier = WMDialogButtonStyle.toModifier(), onClick = ok) }
            ) {
                SpanText(j.remedy(p.reason, p.fromUrl), WMBodySmallTextStyle.toModifier())
                SpanText(j.notCounted, WMBodySmallTextStyle.toModifier())
            }
            is Prompt.ConfirmDeleteUpload -> WMDialog(
                title = j.deleteUploadTitle,
                description = j.deleteUploadMessage(p.fileName),
                onDismiss = { onResponse(Prompt.ConfirmDeleteUpload.Selection.Cancel.ordinal) },
                actions = {
                    WMButton(c.cancel, kind = WMButtonKind.Ghost, modifier = WMDialogButtonStyle.toModifier()) {
                        onResponse(Prompt.ConfirmDeleteUpload.Selection.Cancel.ordinal)
                    }
                    WMButton(j.deleteUpload, kind = WMButtonKind.Danger, modifier = WMDialogButtonStyle.toModifier()) {
                        onResponse(Prompt.ConfirmDeleteUpload.Selection.Delete.ordinal)
                    }
                }
            ) {}
            is Prompt.DeleteUploadFailed -> WMDialog(
                title = j.deleteUploadFailedTitle,
                description = p.message,
                onDismiss = ok,
                actions = { WMButton(c.close, modifier = WMDialogButtonStyle.toModifier(), onClick = ok) }
            ) {}
            Prompt.FileUnavailable -> WMDialog(
                title = j.fileUnavailableTitle,
                description = j.fileUnavailableMessage,
                onDismiss = ok,
                actions = { WMButton(c.close, modifier = WMDialogButtonStyle.toModifier(), onClick = ok) }
            ) {}
            is Prompt.ConfirmReevaluate -> WMDialog(
                title = j.reevaluateTitle,
                description = listOf(p.fileName, j.reevaluateDescription).filter { it.isNotBlank() }.joinToString(": "),
                onDismiss = { onResponse(Prompt.ConfirmReevaluate.Selection.Cancel.ordinal) },
                forceInteraction = true,
                actions = {
                    WMButton(c.cancel, kind = WMButtonKind.Ghost, modifier = WMDialogButtonStyle.toModifier()) { onResponse(Prompt.ConfirmReevaluate.Selection.Cancel.ordinal) }
                    WMButton(j.reevaluate, modifier = WMDialogButtonStyle.toModifier()) { onResponse(Prompt.ConfirmReevaluate.Selection.Reevaluate.ordinal) }
                }
            ) {}
            else -> Unit
        }
    }
}

/** The dialog title for an upload that produced no mix. */
private fun WMWebStrings.JobDescriptions.title(reason: WMUploadFailureReason): String {
    return when (reason) {
        WMUploadFailureReason.NotJobDescription -> notJobDescriptionTitle
        WMUploadFailureReason.PageUnavailable -> pageUnavailableTitle
        WMUploadFailureReason.Malicious -> maliciousTitle
        WMUploadFailureReason.AnalysisFailed, WMUploadFailureReason.Unknown -> analysisFailedTitle
    }
}

/**
 * The reminder under both upload tabs (Penpot `Job Descriptions / Large`, `Paste URL`): uploads are not checked for
 * personal information, with a link to the privacy policy. It wraps onto a second line on phones.
 */
@Composable
private fun WMPrivacyNote(j: WMWebStrings.JobDescriptions) {
    val colors = ColorMode.current.toPalette().fs
    Row(modifier = Modifier.fillMaxWidth().gap(WMTightGapVar.value()).flexWrap(FlexWrap.Wrap), verticalAlignment = Alignment.CenterVertically) {
        SpanText(j.privacyNote, WMCaptionTextStyle.toModifier().color(colors.onSurfaceVariant))
        val link = WMCaptionTextStyle.toModifier().color(colors.onSurface).textDecorationLine(TextDecorationLine.Underline)
        A(href = WMRoutes.PRIVACY_POLICY, attrs = link.toAttrs()) {
            Text(j.privacyPolicy)
        }
    }
}

/**
 * What the user can do about an upload that produced no mix (planning/job-description-intake-plan.md §1 rule 4): a
 * posting URL is printed to PDF and uploaded, a file that is not a job description is replaced by the job description.
 */
private fun WMWebStrings.JobDescriptions.remedy(reason: WMUploadFailureReason, fromUrl: Boolean): String {
    return when (reason) {
        WMUploadFailureReason.NotJobDescription, WMUploadFailureReason.PageUnavailable -> {
            if (fromUrl) remedyPrintToPdf else remedyUploadTheJobDescription
        }
        WMUploadFailureReason.Malicious -> remedyMalicious
        WMUploadFailureReason.AnalysisFailed, WMUploadFailureReason.Unknown -> remedyRetry
    }
}

/**
 * The row's explanation and remedy for a failed or rejected upload; `null` for any other. [atLimitOf] is the plan's
 * upload count once it is used: a retryable upload then says Retry waits for Pro (plan rule 7), instead of the remedy.
 */
private fun WMWebStrings.JobDescriptions.failureDetail(upload: WMUpload, atLimitOf: Int?): String? {
    if (upload.status != WMUploadStatus.Failed && upload.status != WMUploadStatus.Rejected) {
        return null
    }
    val retryable = upload.status == WMUploadStatus.Failed && upload.failureReason?.retryable != false
    val remedy = when {
        retryable && atLimitOf != null -> retryAtLimit(atLimitOf)
        else -> upload.failureReason?.let { remedy(it, fromUrl = upload.source == WMUploadSource.Url) }
    }
    return listOfNotNull(upload.statusMessage, remedy).joinToString(" ").ifBlank { null }
}
